/**
 * Customize for SRD spells and items (2.M deliverable 21): `CardCustomizeProvider` renders one kit
 * `Dialog` "Customize <source title>", opened by `useCardCustomize().open(request)`. It sends one
 * `variants.create` and resolves `{documentId, title, repoint}` or null; it runs no editor command
 * and does not navigate (the caller does). 1.AC-2's dialog mounts it and calls it for a card source.
 */
import '../cards.css';
import { Button, Checkbox, Dialog, Skeleton, TextField } from '@codex/ui';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReferenceEntry } from '../../../sheet-form/data/variant.js';
import { useNotify } from '../../../shell/notices.js';
import { COPY } from '../copy.js';
import { useCreateCardVariant, useTypes } from '../data/hooks.js';
import {
  CARD_EMPTY_NAME,
  type CardCustomizeErrors,
  cardTypeFor,
  customizeErrors,
  TYPE_NAME,
  typeMissing,
} from '../model/customize.js';
import type { CardKind } from '../model/draft.js';
import { HomeField, type HomePick } from './HomeField.js';

export interface CardCustomizeRequest {
  readonly sourceDocId: string;
  /** Shown until the entry is read; the entry's own title wins. */
  readonly title: string;
  /** From an embed: offer "Point this embed at the copy". */
  readonly offerRepoint: boolean;
}

export interface CardCustomizeResult {
  readonly documentId: string;
  readonly title: string;
  /** True only when the repoint was offered and left checked. */
  readonly repoint: boolean;
}

export interface CardCustomizeApi {
  readonly open: (request: CardCustomizeRequest) => Promise<CardCustomizeResult | null>;
}

const NONE: CardCustomizeApi = { open: async () => null };
const CardCustomizeContext = createContext<CardCustomizeApi>(NONE);

/** The dialog's `open`; outside a provider it resolves null at once. */
export function useCardCustomize(): CardCustomizeApi {
  return useContext(CardCustomizeContext);
}

interface Active {
  readonly request: CardCustomizeRequest;
  readonly resolve: (result: CardCustomizeResult | null) => void;
}

export function CardCustomizeProvider({ children }: { readonly children?: ReactNode }) {
  const [active, setActive] = useState<Active | null>(null);
  const activeRef = useRef<Active | null>(null);
  activeRef.current = active;
  const open = useCallback(
    (request: CardCustomizeRequest): Promise<CardCustomizeResult | null> =>
      activeRef.current !== null
        ? Promise.resolve(null)
        : new Promise((resolve) => setActive({ request, resolve })),
    [],
  );
  const finish = useCallback((result: CardCustomizeResult | null) => {
    activeRef.current?.resolve(result);
    activeRef.current = null;
    setActive(null);
  }, []);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <CardCustomizeContext.Provider value={value}>
      {children}
      {active !== null && <CardCustomizeDialog request={active.request} onDone={finish} />}
    </CardCustomizeContext.Provider>
  );
}

function CardCustomizeDialog({
  request,
  onDone,
}: {
  readonly request: CardCustomizeRequest;
  readonly onDone: (result: CardCustomizeResult | null) => void;
}) {
  const notify = useNotify();
  const formId = useId();
  const source = useReferenceEntry(request.sourceDocId);
  const types = useTypes();
  const create = useCreateCardVariant(request.sourceDocId);
  const [name, setName] = useState<string | null>(null);
  const [home, setHome] = useState<HomePick | null>(null);
  const [repoint, setRepoint] = useState(true);
  const [errors, setErrors] = useState<CardCustomizeErrors>({});

  const entry = source.entry;
  const kind: CardKind | null =
    entry?.kind === 'spell' || entry?.kind === 'item' ? entry.kind : null;
  const sourceTitle = entry?.document.title ?? request.title;
  const type = kind && types.data ? cardTypeFor(kind, types.data) : null;
  const failed = source.failure !== null || types.isError || (entry !== undefined && !kind);
  const ready = kind !== null && types.data !== undefined;
  const nameValue = name ?? sourceTitle;
  const nameInput = () =>
    document.getElementById(formId)?.querySelector<HTMLInputElement>('input[name="title"]');

  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the fields first show
  useEffect(() => {
    if (!ready) return;
    const input = nameInput();
    input?.focus();
    input?.select();
  }, [ready]);

  const submit = async () => {
    if (!kind || !type || create.isPending) return;
    if (nameValue.trim() === '') {
      setErrors({ name: CARD_EMPTY_NAME });
      nameInput()?.focus();
      return;
    }
    setErrors({});
    try {
      const answer = await create.mutateAsync({
        name: nameValue,
        typeId: type.id,
        homeId: home?.id ?? null,
      });
      notify(`Created ${answer.document.title}.`);
      onDone({
        documentId: answer.document.id,
        title: answer.document.title,
        repoint: request.offerRepoint && repoint,
      });
    } catch (error) {
      setErrors(customizeErrors(error, kind));
    }
  };

  const busy = create.isPending;
  const message = errors.message ?? (ready && type === null && kind ? typeMissing(kind) : null);
  return (
    <Dialog
      open
      title={sourceTitle === '' ? 'Customize' : `Customize ${sourceTitle}`}
      description="Make an editable homebrew copy. The SRD entry stays as it is."
      busy={busy}
      onClose={() => onDone(null)}
      actions={
        <>
          <Button variant="ghost" onClick={() => onDone(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            busy={busy}
            disabled={!ready || type === null}
          >
            Create copy
          </Button>
        </>
      }
    >
      {failed ? (
        <div>
          <p role="alert">{COPY.compareFailed}</p>
          <Button
            variant="secondary"
            onClick={() => {
              source.retry();
              void types.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : !ready || !kind ? (
        <div>
          <p role="status" className="cx-visually-hidden">
            {COPY.compareLoading}
          </p>
          <div aria-hidden="true">
            <Skeleton lines={3} variant="card" label={COPY.compareLoading} />
          </div>
        </div>
      ) : (
        <form
          id={formId}
          className="cx-cards__section"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <TextField
            name="title"
            label="Name"
            required
            value={nameValue}
            maxLength={200}
            aria-invalid={errors.name !== undefined || undefined}
            description={errors.name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <p>{`Type: ${TYPE_NAME[kind]}`}</p>
          <HomeField home={home} onPick={setHome} error={errors.home} />
          {request.offerRepoint && (
            <Checkbox
              label="Point this embed at the copy"
              checked={repoint}
              onChange={setRepoint}
            />
          )}
          {message !== null && (
            <p role="alert" className="cx-cards__issue">
              {message}
            </p>
          )}
        </form>
      )}
    </Dialog>
  );
}
